export enum ExecProjectPlatformType {
  RRA = 'RRA',
  RaaS = 'RaaS',
  General = 'General',
}

export interface IExecProjectCollaborator {
  uid: string;
  created_at: number;
  updated_at: number;
}

export interface IExecProject {
  ref: string;
  account_id: string;
  created_by_uid: string;
  collaborators?: IExecProjectCollaborator[];
  acting_user_id?: string | null;
  comments?: IExecProjectComment[];
  display_name?: string;
  project_name: string;
  job_title: string;
  client_label?: string;
  client_meta_ref?: string;
  client_domain: string;
  client_name: string;
  client_company_id: string;
  client_company_account_id?: string;
  hiring_company_domain?: string;
  hiring_company_id?: string;
  hiring_company_name?: string;
  session_ids: Record<string, string>;
  presentations?: IExecProjectPresentationReport[];
  agent_data: Record<string, unknown>;
  is_deleted: boolean;
  deleted_at?: number;
  platform_type?: ExecProjectPlatformType;
  created_at: number;
  updated_at: number;
}

export type ExecProjectPresentationReportKind = "presentation" | "report";

export interface IExecProjectPresentationSlide {
  id: string;
  type: string;
  artifact_event_id?: string;
  candidate_id?: string;
  candidate_ids?: string[];
  continuation_count?: number;
  continuation_index?: number;
  content: string;
  html_content?: string;
  is_loading?: boolean;
  section_id?: string;
  template_layout?: string;
  template_slide_name?: string;
  template_slide_number?: number;
  title: string;
}

export interface IExecProjectPresentationReport {
  id: string;
  name: string;
  kind?: ExecProjectPresentationReportKind;
  session_id?: string;
  /** Generated whole-deck pptx artifact ref (agentic generated_artifacts), persisted on Save. */
  artifact_ref?: string;
  artifact_file_name?: string;
  slides?: IExecProjectPresentationSlide[];
  created_by_uid: string;
  updated_by_uid: string;
  created_at: number;
  updated_at: number;
  is_deleted?: boolean;
}

export interface IExecProjectPresentationReportInput {
  name: string;
  kind?: ExecProjectPresentationReportKind;
  session_id?: string;
  slides?: IExecProjectPresentationSlide[];
}

export interface IListExecProjectPresentationReportsRequest {
  ref: string;
  joid?: string;
}

export interface IListExecProjectPresentationReportsResponse {
  presentations: IExecProjectPresentationReport[];
}

export interface ICreateExecProjectPresentationReportRequest {
  ref: string;
  name: string;
  kind?: ExecProjectPresentationReportKind;
  session_id?: string;
  slides?: IExecProjectPresentationSlide[];
  joid?: string;
}

export interface IUpdateExecProjectPresentationReportRequest {
  ref: string;
  presentation_id: string;
  updates: {
    kind?: ExecProjectPresentationReportKind;
    name?: string;
    session_id?: string | null;
    artifact_ref?: string | null;
    artifact_file_name?: string | null;
    slides?: IExecProjectPresentationSlide[];
  };
  joid?: string;
}

export interface IDeleteExecProjectPresentationReportRequest {
  ref: string;
  presentation_id: string;
  joid?: string;
}

export type ExecProjectCommentAuthorType = "client" | "internal";
export type ExecProjectCommentStatus = "deleted" | "open" | "resolved";

export interface IExecProjectComment {
  id: string;
  body: string;
  author_id: string;
  author_type: ExecProjectCommentAuthorType;
  agent_namespace: string;
  status: ExecProjectCommentStatus;
  project?: string;
  source?: string;
  created_at: number;
  updated_at: number;
  author_name?: string | null;
}

export interface IListExecProjectCommentsRequest {
  ref: string;
  agent_namespace?: string;
  show_resolved?: boolean;
  joid?: string;
}

export interface IListExecProjectCommentsResponse {
  comments: IExecProjectComment[];
}

export interface ICreateExecProjectCommentRequest {
  ref: string;
  agent_namespace: string;
  body: string;
  author_type: ExecProjectCommentAuthorType;
  project?: string;
  source?: string;
  joid?: string;
}

export interface IUpdateExecProjectCommentRequest {
  ref: string;
  comment_id: string;
  updates: {
    body?: string;
    status?: Exclude<ExecProjectCommentStatus, "deleted">;
  };
  joid?: string;
}

export interface IDeleteExecProjectCommentRequest {
  ref: string;
  comment_id: string;
  joid?: string;
}

export interface ICreateExecProjectRequest {
  project_name: string;
  display_name: string;
  job_title: string;
  client_name: string;
  client_company_id: string;
  client_company_account_id: string;
  client_domain: string;
  hiring_company_name?: string;
  hiring_company_id?: string;
  platform_type: ExecProjectPlatformType;
  joid?: string;
}

export interface IUpdateExecProjectRequest {
  ref: string;
  project_name?: string;
  acting_user_id?: string | null;
  job_title?: string;
  client_name?: string;
  client_company_id?: string;
  client_company_account_id?: string;
  client_domain?: string;
  hiring_company_name?: string;
  hiring_company_id?: string;
  platform_type?: ExecProjectPlatformType;
  session_ids?: Record<string, string>;
  presentations?: IExecProjectPresentationReportInput[];
  joid?: string;
}

export interface IListExecProjectsResponse {
  projects: IExecProject[];
  total: number;
}

export interface IExecProjectListItem {
  collaborators?: IExecProjectCollaborator[];
  ref: string;
  account_id: string;
  created_by_uid: string;
  acting_user_id: string | null;
  client_name: string;
  project_name: string;
  client_company_id: string;
  client_company_account_id?: string;
  hiring_company_name?: string;
  hiring_company_id?: string;
  milestone: string;
  status: string;
  platform_type: ExecProjectPlatformType;
  pipeline_count: number;
  people_list_icp_id?: string;
  updated_at: string;
}

export interface IExecProjectSearchResponse {
  /** Exact total for normal searches and the first cross-account cursor page. */
  count?: number;
  next_cursor?: string | null;
  page_size?: number;
  projects: IExecProjectListItem[];
}

export interface IExecProjectSearchFilter {
  field: string;
  operator: string;
  value: string;
}

export interface IExecProjectSortField {
  field: string;
  order: 'asc' | 'desc';
}

export interface IExecProjectSearchParams {
  joid: string;
  cross_account?: boolean;
  cursor?: string;
  page?: number;
  size?: number;
  filters?: IExecProjectSearchFilter[];
  sort_fields?: IExecProjectSortField[];
}

export interface IExecProjectDeleteResponse {
  ref: string;
  is_deleted: boolean;
  deleted_at: number;
  updated_at: number;
}

export interface IExecProjectAiCompanyEvalRequest {
  exec_project_ref: string;
  company_list_ids: string[];
  joid?: string;
}

export interface IExecProjectAiCompanyEvalItem {
  answer?: boolean;
  answer_reasoning?: string;
  meta_ref?: string;
  payload_ref?: string;
}

export interface IExecProjectAiCompanyEvalResponse {
  accepted?: IExecProjectAiCompanyEvalItem[];
  rejected?: IExecProjectAiCompanyEvalItem[];
}

export interface IResetExecProjectMilestonesRequest {
  /** Exec project ref whose generated milestone data should be reset. */
  ref: string;
  /**
   * Milestone agent namespaces to reset, for example "success_profile",
   * "ssa", or "peoplemap_search".
   */
  agent_namespaces: string[];
  /**
   * When false or omitted, fully wipes the selected milestone state for a clean
   * re-run. When true, keeps system fields such as status and approval
   * timestamps while regenerating the selected agent content.
   */
  preserve_system_fields?: boolean;
  /** Optional impersonated user id propagated as the request query param. */
  joid?: string;
}

export interface IClientPreferredTitlesBucket {
  bucket_number: string;
  titles: string[];
}

export interface IRequestApprovalRequest {
  ref: string;
  on_behalf?: boolean;
  agent_namespace: string;
  client_preferred_titles?: IClientPreferredTitlesBucket[];
  client_emails?: string[];
  client_email_subject?: string;
  client_email_body?: string;
  joid?: string;
}

export interface IExecProjectError {
  error_code: string;
  message: string;
  details?: Record<string, unknown>;
}

export const ExecProjectErrorCodes = {
  FORBIDDEN: "EXEC_PROJECT_FORBIDDEN",
} as const;

export interface IPipelineAgent {
  agent_name: string;
  display_name: string;
  agent_id: string;
  parameter_map?: Record<string, string>;
}

export interface IPipelineClientAgentAccessConfig {
  agent_names?: string[];
}

export interface IPipelineConfig {
  enabled?: boolean;
  pipeline?: IPipelineAgent[];
  client_agent_access?: IPipelineClientAgentAccessConfig;
}

export interface IPlatformSettings {
  exec_projects_pipeline?: IPipelineConfig;
}

export interface ISession {
  _id: string;
  resource_id: string;
  session_id?: string;
  resource_name: string;
  resource_type: string;
  created_at: string;
  updated_at: string;
}

export interface IPipelineContext {
  parameter_map: Record<string, string>;
  source_object: Record<string, unknown>;
}

export interface IUpdateSessionAgentParamsParams {
  projectRef?: string;
  joid?: string;
  resource_id: string;
  agent_params?: Record<string, unknown>;
  pipeline_context?: IPipelineContext;
}
